import { useEffect, useRef, useState } from "react";
import socket from "services/socket";
import useLiveStudioStore from "store/useLiveStudioStore";
import { Button } from "components/ui/button";
import { ShimmerPostDetail } from "components/app/shrimmer/Shrimmer";
import HelmetHtml from "components/app/helmet-html/HelmetHtml";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import ButtonFollow from "components/app/button-follow/ButtonFollow";
import { Service } from "utils/services";
import LinkifyText from "components/app/linkify/LinkifyText";
import Comment from "pages/profile-post-contents-page-view/post-component/Comment";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import { favorite, profileVerified, share } from "assets";
import { Maximize, Pause, Play, SendHorizontalIcon, Sticker, Volume2, VolumeOffIcon, X } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceSmileBeam, faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as outlineHeart } from "@fortawesome/free-regular-svg-icons";
import CopyLinkButton from "pages/profile-post-contents-page-view/post-component/CopyLinkButton";
import { Textarea } from "components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import StickerPicker from "pages/profile-post-contents-page-view/post-component/Stickers";
import flvjs from "flv.js";
import { AppLog } from "services";
import { StatusMsg } from "utils";

export default function LiveStream() {
  const {
    post,
    postLive,
    isLoadingPage,
    initializePage,
    disposePage,
    submitEndStreaming,
    comments,
    commentsRecord,
    fetchComments,
    requestReplyComment,
    requestReactionComment,
    fetchSubComments,
    requestSubmitComment,
  } = useLiveStudioStore();

  const videoRef = useRef<HTMLVideoElement>(null);
  const flvPlayerRef = useRef<flvjs.Player | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLive, setIsLive] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(100);
  const [showVolumeInput, setShowVolumeInput] = useState(false);

  const [viewerCount, setViewerCount] = useState(0);
  const [streamEnded, setStreamEnded] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [textComment, setTextComment] = useState("");
  const [sticker, setSticker] = useState<any>({});
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const stickerPickerRef = useRef<HTMLDivElement>(null);

  const onHandleScrollComment = async () => {
    if (!scrollRef.current || commentsRecord.isLoadingMore()) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10 && commentsRecord.canLoadMore()) {
      fetchComments();
    }
  };

  const handleReplyClick = (commentId: number) => {
    setReplyingTo(commentId);
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (textComment) {
        requestSubmitComment(sticker, textComment);
        setTextComment("");
        setSticker({});
      }
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setShowEmojiPicker(false);
    setTextComment((prev) => prev + emojiData.emoji);
    // setShowEmojiPicker(false);
  };

  const createPlayer = () => {
    if (!videoRef.current || !flvjs.isSupported()) return;

    const streamUrl = postLive.media_url;

    const player = flvjs.createPlayer({
      type: "flv",
      url: streamUrl,
      isLive: true,
    });

    player.attachMediaElement(videoRef.current);
    player.load();
    player.play();

    flvPlayerRef.current = player;

    // Live badge polling using seekable.end(0)
    intervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (video && video.seekable.length > 0) {
        const liveEdge = video.seekable.end(0);
        const current = video.currentTime;
        setIsLive(liveEdge - current < 5);
      }
    }, 1000);
  };

  const destroyPlayer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (flvPlayerRef.current) {
      flvPlayerRef.current.destroy();
      flvPlayerRef.current = null;
    }
  };

  const handleGoLive = () => {
    destroyPlayer();
    setTimeout(() => {
      createPlayer();
    }, 200);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const onSeekVolume = (volume: number) => {
    try {
      setVolume(volume);
      if (videoRef.current) {
        videoRef.current.volume = volume / 100;
        videoRef.current.muted = volume === 0;
        setIsMuted(videoRef.current.muted);
      }
    } catch (error) {
      AppLog.show({
        message: `[onSeekVolume]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  useEffect(() => {
    if (!postLive) return;

    const streamingKey = postLive.media_key;

    socket.on("viewerCountUpdate", (count) => {
      setViewerCount(count);
    });

    socket.on("OnFlipFlopStreamEnded", (data) => {
      setStreamEnded(true);
      destroyPlayer();
    });

    socket.emit("viewerJoin", streamingKey);

    return () => {
      socket.emit("viewerLeave", streamingKey);
      socket.off("viewerCountUpdate");
      socket.off("OnFlipFlopStreamEnded");
    };
  }, [postLive]);

  useEffect(() => {
    if (!postLive) return;
    createPlayer();

    return () => {
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postLive]);

  useEffect(() => {
    initializePage();

    return () => {
      disposePage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // * --- Layouts --- * //

  const postCommentContents = (post: any) => {
    const userPost = post.re_post_user ? post.re_post_user : post.user;
    return (
      <div className="flex flex-col h-screen relative justify-between bg-white  dark:bg-background pt-4 ">
        <div className="flex flex-col relative space-y-2 pl-4 pr-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-row items-start space-x-2 w-full  ">
              <ImageUrlProfile
                user={userPost}
                style={{ width: 50, height: 50 }}
                className="rounded-full object-cover border shadow-md shadow-gray-300 dark:shadow-none"
                isLinkProfile={true}
              />
              <div className="flex flex-row justify-between items-center  w-full">
                <div className="flex flex-col ">
                  <div className="flex items-center space-x-2">
                    <p className=" text-xl font-semibold">{userPost.name}</p>
                    {userPost.is_verified === "Yes" ? <img src={profileVerified} alt="" style={{ width: 12, height: 12 }} /> : <></>}
                    {userPost?.friend_status === "Friend" ? null : <ButtonFollow className="h-[25px]" friendStatus={userPost?.friend_status} onClick={() => {}} />}
                  </div>
                  <div className="flex items-center space-x-2">
                    <p>
                      {post?.is_sponsored === "Yes"
                        ? "Sponsored"
                        : post?.is_available_suggestion === "Yes"
                        ? "Suggestion for you"
                        : `${postedTimeAgo(post?.provider_created_datetime)}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant="destructive"
              onClick={(e: any) => {
                submitEndStreaming();
              }}
            >
              END LIVE
            </Button>

            {/* <Link to={`/post?post_id=${postLive.provider_post_id}`}>
              <Button>GO TO POST</Button>
            </Link> */}
          </div>
          {/* Post description */}
          <LinkifyText text={post?.post_attachments[0]?.description ?? ""} />
          <div className="flex-1 flex-row justify-between w-full">
            <div className="flex flex-row justify-start w-full space-x-2">
              <div className="flex w-8 h-8 cursor-pointer  rounded-full bg-gray-200 text-center items-center justify-center" onClick={() => {}}>
                <FontAwesomeIcon
                  icon={`${post?.is_reaction_like}` === "1" ? solidHeart : outlineHeart}
                  size="sm"
                  className={` size-5 ${`${post?.is_reaction_like}` === "1" ? "heart-icon text-red-600 heart-fill" : "heart-outline text-black dark:text-gray-500"}`}
                />
              </div>
              <div className="flex w-8 h-8 cursor-pointer  rounded-full pb-1 pl-1 pr-1 bg-gray-200 text-center items-center justify-center" onClick={() => {}}>
                <img src={share} alt="" width={25} height={25} />
              </div>

              <div
                className={`flex w-8 h-8 p-1 cursor-pointer rounded-full ${
                  `${post?.is_reaction_favorite}` === "1" ? "bg-yellow-200" : " bg-gray-200"
                }  text-center items-center justify-center`}
                onClick={() => {}}
              >
                <img src={favorite} alt="Favorite" width={25} height={25} />
              </div>
              <CopyLinkButton endPoint={`post/${post.provider_post_id}`} />

              {post?.re_post_user ? (
                <div className="flex flex-row rounded-2xl pl-1 pr-2 bg-gray-200 text-center items-center justify-center space-x-1">
                  <ImageUrlProfile
                    user={post?.user}
                    style={{ width: 20, height: 20 }}
                    className="rounded-full object-cover border shadow-md shadow-gray-300 dark:shadow-none"
                    isLinkProfile={true}
                  />
                  <p className="font-bold text-sm">{post?.user?.name}</p>
                  <p className="text-sm">Shared</p>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          {/* <TextReaction post={post} onOpenReactionDialog={onOpenReactionDialog} /> */}
        </div>
        {/* Sub comments */}

        <div ref={scrollRef} onScroll={onHandleScrollComment} className="flex-1  dark:bg-background px-4 overflow-y-auto mt-4">
          {post.comment_count > 0 && (
            <p className="font-normal hover:underline cursor-pointer mr-1" onClick={() => {}}>
              {post.comment_count} Comments
            </p>
          )}

          {[...comments].map((comment, index) => (
            <Comment
              replyingTo={replyingTo!}
              onReplyClick={() => handleReplyClick(comment.id)}
              key={comment.id}
              comment={comment}
              index={index}
              submitReply={(data: any) => {
                requestReplyComment(data);
                handleReplyClick(-1);
              }}
              onClikcReact={() => requestReactionComment({ comment })}
              fetchSubComments={fetchSubComments}
            >
              {comment.sub_comments?.data?.length > 0 && (
                <div className="">
                  {comment.sub_comments.data.map((subComment: any, subIndex: number) => (
                    <Comment
                      replyingTo={replyingTo!}
                      onReplyClick={() => handleReplyClick(subComment.id)}
                      key={subComment.id}
                      comment={subComment}
                      index={subIndex}
                      submitReply={(data: any) => {
                        requestReplyComment(data);
                        handleReplyClick(-1);
                      }}
                      onClikcReact={() =>
                        requestReactionComment({
                          comment: subComment,
                        })
                      }
                      fetchSubComments={fetchSubComments}
                      isSubComment={true}
                    >
                      {/* Recursively render deeper sub-comments */}
                      {subComment.sub_comments?.data?.length > 0 && (
                        <div className="">
                          {subComment.sub_comments.data.map((nestedComment: any, nestedIndex: number) => (
                            <Comment
                              replyingTo={replyingTo!}
                              onReplyClick={() => handleReplyClick(nestedComment.id)}
                              key={nestedComment.id}
                              comment={nestedComment}
                              index={nestedIndex}
                              submitReply={(data: any) => {
                                requestReplyComment(data);
                                handleReplyClick(-1);
                              }}
                              onClikcReact={() =>
                                requestReactionComment({
                                  comment: nestedComment,
                                })
                              }
                              fetchSubComments={fetchSubComments}
                              isSubComment={true}
                            >
                              {nestedComment.sub_comments?.data?.length > 0 && (
                                <div className="">
                                  {nestedComment.sub_comments.data.map((nestedComment_f: any, nestedIndex_f: number) => (
                                    <Comment
                                      replyingTo={replyingTo!}
                                      onReplyClick={() => handleReplyClick(nestedComment_f.id)}
                                      key={nestedComment_f.id}
                                      comment={nestedComment_f}
                                      index={nestedIndex_f}
                                      submitReply={(data: any) => {
                                        requestReplyComment(data);
                                        handleReplyClick(-1);
                                      }}
                                      onClikcReact={() =>
                                        requestReactionComment({
                                          comment: nestedComment_f,
                                        })
                                      }
                                      isSubComment={true}
                                    />
                                  ))}
                                </div>
                              )}
                            </Comment>
                          ))}
                        </div>
                      )}
                    </Comment>
                  ))}
                </div>
              )}
            </Comment>
          ))}
        </div>

        <div className="flex flex-row  dark:bg-background  justify-center items-start space-x-2 w-full pt-2 px-4 mb-2">
          <ImageUrlProfile isLinkProfile={true} user={Service?.userAuth} className="cursor-pointer rounded-full w-8 h-8" alt="logo" />
          <div className="flex flex-col space-y-2  w-full items-start justify-start">
            <div className="border  relative p-2 rounded-2xl w-full dark:bg-[#3A3B3C] ">
              <div className="flex flex-col">
                <Textarea
                  value={textComment}
                  onKeyDown={onHandleKeyDown}
                  maxLength={100}
                  onChange={(e) => setTextComment(e.target.value)}
                  className="border border-none min-h-[10px]  focus-visible:ring-0 focus-visible:ring-offset-0 w-full  px-0 py-0 rounded-none dark:bg-[#3A3B3C]"
                  placeholder="Add comment"
                />
                <div className="flex justify-between items-center ">
                  <div className="flex space-x-2 items-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                          <FontAwesomeIcon icon={faFaceSmileBeam} className="text-gray-500 dark:text-gray-500 cursor-pointer size-5" onClick={() => {}} />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-92">
                        <div ref={emojiPickerRef} className="absolute bottom-[0px] left-0 z-[100px]" spellCheck={false}>
                          <EmojiPicker reactionsDefaultOpen={false} onEmojiClick={onEmojiClick} emojiStyle={EmojiStyle.FACEBOOK} searchDisabled={true} skinTonesDisabled={true} />
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                          <Sticker className=" text-gray-700 dark:text-gray-500 size-5 font-extrabold cursor-pointer" onClick={() => setShowStickerPicker(true)} />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-92 bg-transparent border-none shadow-none">
                        {showStickerPicker && (
                          <StickerPicker
                            stickerPickerRef={stickerPickerRef}
                            onStickerSelect={(sticker) => {
                              setShowStickerPicker(false);
                              setSticker(sticker);
                            }}
                          />
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center ">
                    <SendHorizontalIcon
                      className={`cursor-pointer size-5 ${textComment || Object.keys(sticker).length !== 0 ? "text-yellow-400" : "text-gray-500 dark:text-gray-500"}`}
                      onClick={
                        !textComment && Object.keys(sticker).length === 0
                          ? () => {}
                          : () => {
                              requestSubmitComment(sticker, textComment);
                              setTextComment("");
                              setSticker({});
                            }
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            {Object.keys(sticker).length !== 0 ? (
              <div className="relative sticker-image w-16 h-16">
                <img src={sticker.thumbnail_url} alt={sticker.pack_name} />
                <div
                  className="absolute bg-gray-200 hover:bg-gray-300 -top-1 -right-2 w-5 h-5 rounded-full dark:bg-[#3A3B3C] flex items-center cursor-pointer justify-center hover:dark:bg-[#4E4F50]"
                  onClick={() => setSticker({})}
                >
                  <X className="w-4 h-4 " />
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col lg:grid lg:grid-cols-3  overflow-hidden h-[100vh]">
        <HelmetHtml title="Live-Studio" description="Live Studio" />
        {/* Post Content */}
        <div className="lg:col-span-2 md:h-[60vh] lg:h-auto items-center text-center bg-black ">
          <div className="relative flex w-full h-full items-center justify-center">
            <div className="flex w-full h-full justify-center items-center  ">
              <div className="flex w-full h-full lg:h-screen ">
                <div className=" flex w-full h-full">
                  <div className="relative w-full">
                    {/* Overlay Badges */}
                    {postLive && (
                      <div className="absolute top-3 right-3 flex space-x-2 z-10">
                        {/* LIVE Badge */}
                        <div className={`text-white text-xs font-bold px-3 py-1 rounded ${isLive ? "bg-red-600" : "bg-gray-500"}`} onClick={handleGoLive}>
                          LIVE
                        </div>

                        {/* Viewer Count */}
                        <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded flex items-center space-x-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          <span>{viewerCount}</span>
                        </div>
                      </div>
                    )}
                    {/* Video Element */}
                    <div id="reconnectOverlay" className="hidden absolute top-0 left-0 w-full h-full bg-black bg-opacity-70 items-center justify-center text-2xl z-10">
                      📶 Reconnecting...
                    </div>

                    <video
                      ref={videoRef}
                      // controls
                      muted
                      autoPlay
                      className="w-full rounded h-full"
                    />

                    <div className="absolute bottom-4 w-full px-4 flex items-center justify-between text-white z-50 ">
                      {/* Left controls: Play + Volume + Slider */}
                      <div className="flex items-center space-x-2">
                        {isPlaying ? (
                          <Pause onClick={togglePlay} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                        ) : (
                          <Play onClick={togglePlay} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                        )}

                        <div
                          className="flex items-center space-x-2"
                          onClick={() => {}}
                          onMouseMove={() => {
                            setShowVolumeInput(true);
                          }}
                          onMouseLeave={() => setShowVolumeInput(false)}
                        >
                          <button className="text-white" onClick={toggleMute}>
                            {isMuted || !isPlaying ? (
                              <VolumeOffIcon className="rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                            ) : (
                              <Volume2 className="rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                            )}
                          </button>

                          {showVolumeInput && (
                            <div className=" bg-black size-5 rounded-2xl bg-opacity-10 text-white max-sm:w-24 w-28 p-2 items-center flex pt-4 pb-4">
                              <input type="range" min={0} max={100} value={volume} onChange={(e) => onSeekVolume(parseFloat(e.target.value))} className="max-sm:w-20 w-24 " />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <button onClick={handleGoLive} className="flex items-center space-x-2 text-white font-semibold">
                          <span className={`w-2 h-2 rounded-full ${isLive ? "bg-red-600" : "bg-gray-400"}`} />
                          <span>Live</span>
                        </button>
                        <Maximize onClick={toggleFullscreen} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                      </div>
                    </div>
                  </div>

                  {streamEnded && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center text-white text-2xl z-50">🔴 This live stream has ended.</div>
                  )}
                </div>
              </div>
            </div>

            {!isLoadingPage && !postLive && (
              <div className={`flex absolute w-full h-full items-center justify-center text-white lg:h-screen p-6 z-50`}>
                <p className="text-center">No streaming...</p>
              </div>
            )}
          </div>
        </div>

        {/* Post Comment */}
        {postLive ? postCommentContents(post) : <div></div>}
      </div>

      {isLoadingPage && (
        <div className="fixed inset-0">
          <ShimmerPostDetail />
        </div>
      )}
    </div>
  );
}

